/**
 * Predefined Job Templates
 * Ready-to-use templates for common job operations
 */

import { JobTemplate, JobCategory } from '../types/job';

export interface ParameterMetadata {
  description: string;
  example?: string;
  hint?: string;
}

export interface JobTemplateWithMetadata extends JobTemplate {
  parameterMetadata?: Record<string, ParameterMetadata>;
}

export const JOB_TEMPLATES: JobTemplateWithMetadata[] = [
  {
    id: 'mp4-to-hls',
    name: 'Convert MP4 to HLS',
    category: JobCategory.VIDEO_CONVERSION,
    description: 'Convert MP4 video to HLS format with multiple quality levels (4K, 2K, 1K)',
    inputFileTypes: ['.mp4', '.avi', '.mov', '.mkv'],
    outputFormat: 'hls',
    parameters: {
      resolutions: [
        { quality: '4K', width: 3840, height: 2160, bitrate: '20Mbps' },
        { quality: '2K', width: 2560, height: 1440, bitrate: '8Mbps' },
        { quality: '1080p', width: 1920, height: 1080, bitrate: '5Mbps' },
      ],
      segmentDuration: 10,
      playlistType: 'vod',
    },
    parameterMetadata: {
      resolutions: {
        description: 'Output quality levels for the video',
        example: '4K (3840x2160), 2K (2560x1440), or 1080p (1920x1080)',
        hint: 'Higher resolutions = larger files but better quality. Include multiple for adaptive streaming.',
      },
      segmentDuration: {
        description: 'Length of each video segment in seconds',
        example: '10 seconds per segment is ideal for streaming',
        hint: 'Smaller values = more responsive but more files. Typical: 6-10 seconds',
      },
      playlistType: {
        description: 'Type of streaming playlist',
        example: '"vod" for videos-on-demand (pre-recorded), "event" for live streams',
        hint: 'Use "vod" for regular videos, "event" for live or continuous streaming',
      },
    },
    icon: '🎬',
  },

  {
    id: 'photo-upscale',
    name: 'Upscale Photo with AI',
    category: JobCategory.IMAGE_UPSCALE,
    description: 'Use AI to upscale and enhance photo quality, improving resolution and clarity',
    inputFileTypes: ['.jpg', '.jpeg', '.png', '.webp', '.tiff'],
    outputFormat: 'png',
    parameters: {
      upscaleFactor: 2,
      denoiseLevel: 'medium',
      enhanceColors: true,
      reduceArtifacts: true,
    },
    parameterMetadata: {
      upscaleFactor: {
        description: 'How many times to enlarge the image',
        example: '2x = double size, 4x = four times larger',
        hint: '2x is recommended for most photos. Higher values may be slower.',
      },
      denoiseLevel: {
        description: 'How much to reduce image noise',
        example: '"low" (minimal), "medium" (balanced), "high" (aggressive)',
        hint: 'Choose based on original photo quality. High noise in original = use "high"',
      },
      enhanceColors: {
        description: 'Automatically improve color vibrancy and saturation',
        example: 'true = enhance colors, false = keep original colors',
        hint: 'Enable for dull or faded photos',
      },
      reduceArtifacts: {
        description: 'Remove AI upscaling artifacts and compression noise',
        example: 'true = clean result, false = preserve all details',
        hint: 'Usually keep enabled for best results',
      },
    },
    icon: '🖼️',
  },

  {
    id: 'photo-multisize',
    name: 'Generate Multi-size Photos',
    category: JobCategory.IMAGE_RESIZE,
    description: 'Generate multiple sizes of the same photo for different use cases (thumbnails, web, print)',
    inputFileTypes: ['.jpg', '.jpeg', '.png', '.webp'],
    outputFormat: 'png',
    parameters: {
      sizes: [
        { name: 'thumbnail', width: 64, height: 64 },
        { name: 'small', width: 128, height: 128 },
        { name: 'medium', width: 256, height: 256 },
        { name: 'large', width: 512, height: 512 },
      ],
      maintainAspectRatio: true,
      format: 'png',
    },
    parameterMetadata: {
      sizes: {
        description: 'Different image sizes to generate',
        example: 'Common sizes: 64x64 (thumbnails), 256x256 (web), 1024x1024 (print)',
        hint: 'Smaller = faster, larger = more detail. Generate what you need.',
      },
      maintainAspectRatio: {
        description: 'Keep original image proportions when resizing',
        example: 'true = no stretching, false = exact size (may distort)',
        hint: 'Almost always use true to avoid distortion',
      },
      format: {
        description: 'Output image format',
        example: '"png" (best quality, larger files), "jpg" (smaller files), "webp" (modern, best compression)',
        hint: 'Use png for web, jpg for photos, webp for modern browsers',
      },
    },
    icon: '📸',
  },
];

/**
 * Get template by ID
 */
export const getTemplate = (templateId: string): JobTemplate | undefined => {
  return JOB_TEMPLATES.find((t) => t.id === templateId);
};

/**
 * Get templates by category
 */
export const getTemplatesByCategory = (category: JobCategory): JobTemplate[] => {
  return JOB_TEMPLATES.filter((t) => t.category === category);
};

/**
 * Get all available categories
 */
export const getCategories = (): JobCategory[] => {
  const categories = new Set<JobCategory>();
  JOB_TEMPLATES.forEach((t) => categories.add(t.category));
  return Array.from(categories);
};
